import { useLayoutEffect, useMemo, useRef, type RefObject } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useConfig, type Mood } from '../store';
import { easeInOutCubic, Tween } from '../motion';
import type { MatsProps } from './truck/materials';
import { barZ, BULB_POSITIONS, HEADLIGHT_POSITION, SERVICE_X, TRUCK_Y } from './truck/layout';

const MOOD_TRANSITION_MS = 800;
const SPILL_TRANSITION_MS = 500;
const HALO_LAYER = 1;
const FLOOR_Y = -0.006;
const DECAL_Y = -0.003;
const AMBER = '#ffd2a0';
const POOL_ABSORB = '#0066bf';
const MOON = '#9fb9e8';

interface Lighting {
  hemi: number;
  key: number;
  pinkRim: number;
  fill: number;
  moonRim: number;
  env: number;
  exposure: number;
  interior: number;
  interiorDistance: number;
  festoon: number;
  bulbEmissive: number;
  halo: number;
  haloScale: number;
  headlightEmissive: number;
  beam: number;
  headlightHalo: number;
  headlightFloor: number;
  poolLight: number;
  pool: number;
  poolAbsorb: number;
}

const LIGHTING: Record<Mood, Lighting> = {
  tramonto: {
    hemi: 0.44,
    key: 1.65,
    pinkRim: 1.1,
    fill: 0.62,
    moonRim: 3.0,
    env: 0.72,
    exposure: 0.95,
    interior: 1.5,
    interiorDistance: 2.6,
    festoon: 0.45,
    bulbEmissive: 2.2,
    halo: 0.35,
    haloScale: 0.15,
    headlightEmissive: 0.8,
    beam: 0,
    headlightHalo: 0,
    headlightFloor: 0,
    poolLight: 5,
    pool: 0.44,
    poolAbsorb: 0.85,
  },
  notte: {
    hemi: 0.11,
    key: 0.41,
    pinkRim: 0.8,
    fill: 0.16,
    moonRim: 2.3,
    env: 0.3,
    exposure: 1.0,
    interior: 3.2,
    interiorDistance: 3,
    festoon: 1.0,
    bulbEmissive: 2.8,
    halo: 0.95,
    haloScale: 0.22,
    headlightEmissive: 3.6,
    beam: 3,
    headlightHalo: 0.8,
    headlightFloor: 0.09,
    poolLight: 11,
    pool: 0.55,
    poolAbsorb: 0.9,
  },
};

const LIGHTING_KEYS = Object.keys(LIGHTING.tramonto) as (keyof Lighting)[];

function mixLighting(t: number, out: Lighting): Lighting {
  LIGHTING_KEYS.forEach((k) => {
    out[k] = THREE.MathUtils.lerp(LIGHTING.tramonto[k], LIGHTING.notte[k], t);
  });
  return out;
}

function radialTexture(size: number, stops: [number, string][], colorSpace: THREE.ColorSpace): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');
  if (!g) throw new Error('2D canvas context unavailable');
  const r = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  stops.forEach(([at, color]) => r.addColorStop(at, color));
  g.fillStyle = r;
  g.fillRect(0, 0, size, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = colorSpace;
  return t;
}

function attached<T>(ref: RefObject<T | null>, name: string): T {
  if (!ref.current) throw new Error(`EveningStage: ${name} is not mounted`);
  return ref.current;
}

function pointAt(x: number, y: number, z: number): THREE.Object3D {
  const o = new THREE.Object3D();
  o.position.set(x, y, z);
  return o;
}

const moodValue = (mood: Mood) => (mood === 'notte' ? 1 : 0);
const spillValue = (showLights: boolean, showAwning: boolean) => (showLights && showAwning ? 1 : 0);

export function EveningStage({ mats }: MatsProps) {
  const mood = useConfig((s) => s.mood);
  const showLights = useConfig((s) => s.showLights);
  const showAwning = useConfig((s) => s.showAwning);
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const camera = useThree((s) => s.camera);
  const invalidate = useThree((s) => s.invalidate);

  const textures = useMemo(
    () => ({
      floor: radialTexture(
        512,
        [
          [0, '#fff'],
          [0.22, '#fff'],
          [0.55, '#8a8a8a'],
          [0.8, '#262626'],
          [1, '#000'],
        ],
        THREE.NoColorSpace,
      ),
      spill: radialTexture(
        512,
        [
          [0, 'rgba(255,255,255,1)'],
          [0.18, 'rgba(255,255,255,0.82)'],
          [0.45, 'rgba(255,255,255,0.32)'],
          [0.75, 'rgba(255,255,255,0.07)'],
          [1, 'rgba(255,255,255,0)'],
        ],
        THREE.SRGBColorSpace,
      ),
      halo: radialTexture(
        128,
        [
          [0, 'rgba(255,255,255,1)'],
          [0.12, 'rgba(255,255,255,0.75)'],
          [0.35, 'rgba(255,255,255,0.22)'],
          [0.7, 'rgba(255,255,255,0.05)'],
          [1, 'rgba(255,255,255,0)'],
        ],
        THREE.SRGBColorSpace,
      ),
    }),
    [],
  );

  const materials = useMemo(
    () => ({
      poolAbsorb: new THREE.MeshBasicMaterial({ map: textures.spill, color: POOL_ABSORB, transparent: true, premultipliedAlpha: true, blending: THREE.SubtractiveBlending, depthWrite: false, toneMapped: false }),
      pool: new THREE.MeshBasicMaterial({ map: textures.spill, color: AMBER, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }),
      headlightFloor: new THREE.MeshBasicMaterial({ map: textures.spill, color: '#fff0d6', transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }),
      bulbHalo: new THREE.SpriteMaterial({ map: textures.halo, color: '#ffcf7a', transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }),
      headlightHalo: new THREE.SpriteMaterial({ map: textures.halo, color: '#fff0d0', transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }),
    }),
    [textures],
  );

  const targets = useMemo(
    () => ({
      moon: pointAt(0.5, 0.9 + TRUCK_Y, 0),
      pool: pointAt(SERVICE_X, 0, barZ + 1.1),
      beam: pointAt(2.6, 0, 0.2),
    }),
    [],
  );

  const moodTween = useMemo(() => new Tween(moodValue(useConfig.getState().mood), easeInOutCubic), []);
  const spillTween = useMemo(() => {
    const s = useConfig.getState();
    return new Tween(spillValue(s.showLights, s.showAwning), easeInOutCubic);
  }, []);
  const mixed = useMemo(() => ({ ...LIGHTING.tramonto }), []);

  const hemi = useRef<THREE.HemisphereLight>(null);
  const key = useRef<THREE.DirectionalLight>(null);
  const pinkRim = useRef<THREE.DirectionalLight>(null);
  const fill = useRef<THREE.DirectionalLight>(null);
  const moonRim = useRef<THREE.DirectionalLight>(null);
  const poolLight = useRef<THREE.SpotLight>(null);
  const beam = useRef<THREE.SpotLight>(null);
  const interior = useRef<THREE.PointLight>(null);
  const festoon = useRef<THREE.Group>(null);
  const bulbHalos = useRef<THREE.Group>(null);
  const headlightHalo = useRef<THREE.Sprite>(null);
  const pool = useRef<THREE.Group>(null);
  const headlightFloor = useRef<THREE.Mesh>(null);

  useLayoutEffect(() => {
    camera.layers.enable(HALO_LAYER);
  }, [camera]);

  useLayoutEffect(() => {
    if (moodTween.retarget(moodValue(mood), MOOD_TRANSITION_MS)) invalidate();
  }, [mood, moodTween, invalidate]);

  useLayoutEffect(() => {
    spillTween.retarget(spillValue(showLights, showAwning), SPILL_TRANSITION_MS);
    invalidate();
  }, [showLights, showAwning, spillTween, invalidate]);

  useFrame(() => {
    const moving = [moodTween.step(), spillTween.step()].includes(true);
    const l = mixLighting(moodTween.value, mixed);
    const lit = showLights ? 1 : 0;
    const spill = spillTween.value;

    attached(hemi, 'hemisphere light').intensity = l.hemi;
    attached(key, 'key light').intensity = l.key;
    attached(pinkRim, 'pink rim').intensity = l.pinkRim;
    attached(fill, 'fill light').intensity = l.fill;
    attached(moonRim, 'moon rim').intensity = l.moonRim;
    attached(poolLight, 'pool light').intensity = l.poolLight * spill;
    attached(beam, 'headlight beam').intensity = l.beam;
    const inside = attached(interior, 'interior light');
    inside.intensity = l.interior * spill;
    inside.distance = l.interiorDistance;
    attached(festoon, 'festoon lights').children.forEach((c) => {
      (c as THREE.PointLight).intensity = l.festoon * lit;
    });

    const halos = attached(bulbHalos, 'bulb halos');
    halos.visible = lit > 0 && l.halo > 0;
    halos.children.forEach((c) => c.scale.setScalar(l.haloScale));
    materials.bulbHalo.opacity = l.halo;
    attached(headlightHalo, 'headlight halo').visible = l.headlightHalo > 0;
    materials.headlightHalo.opacity = l.headlightHalo;

    attached(pool, 'pool').visible = spill > 0;
    materials.pool.opacity = l.pool * spill;
    materials.poolAbsorb.opacity = l.poolAbsorb * spill;
    attached(headlightFloor, 'headlight floor').visible = l.headlightFloor > 0;
    materials.headlightFloor.opacity = l.headlightFloor;

    mats.bulb.emissiveIntensity = l.bulbEmissive;
    mats.headlight.emissiveIntensity = l.headlightEmissive;
    scene.environmentIntensity = l.env;
    gl.toneMappingExposure = l.exposure;

    if (moving) invalidate();
  });

  return (
    <group>
      <hemisphereLight ref={hemi} args={['#bcc8ee', '#3d3140', LIGHTING.tramonto.hemi]} />
      <directionalLight
        ref={key}
        castShadow
        position={[5.4, 4.3, 3.9]}
        intensity={LIGHTING.tramonto.key}
        color="#ffd6b3"
        shadow-mapSize={[2048, 2048]}
        shadow-bias={-0.0003}
        shadow-normalBias={0.02}
        shadow-camera-near={1}
        shadow-camera-far={24}
        shadow-camera-left={-6}
        shadow-camera-right={6}
        shadow-camera-top={6}
        shadow-camera-bottom={-6}
      />
      <directionalLight ref={pinkRim} position={[-5, 3, -5]} intensity={LIGHTING.tramonto.pinkRim} color="#ffc7e2" />
      <directionalLight ref={fill} position={[-2, 2, 5]} intensity={LIGHTING.tramonto.fill} color="#d3ddff" />
      <primitive object={targets.moon} />
      <directionalLight ref={moonRim} position={[-1.4, 6.4, -4.6]} target={targets.moon} intensity={LIGHTING.tramonto.moonRim} color={MOON} />

      <group position={[0, TRUCK_Y, 0]}>
        <primitive object={targets.pool} />
        <primitive object={targets.beam} />

        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-0.2, FLOOR_Y, 0.35]} renderOrder={-1}>
          <circleGeometry args={[7.5, 96]} />
          <meshLambertMaterial color="#081b35" alphaMap={textures.floor} transparent depthWrite toneMapped={false} />
        </mesh>
        <group ref={pool} rotation={[-Math.PI / 2, 0, 0]} position={[SERVICE_X, DECAL_Y, barZ + 0.55]}>
          <mesh material={materials.poolAbsorb} renderOrder={2}>
            <planeGeometry args={[3.4, 2.4]} />
          </mesh>
          <mesh material={materials.pool} renderOrder={3}>
            <planeGeometry args={[3.4, 2.4]} />
          </mesh>
        </group>
        <mesh ref={headlightFloor} rotation={[-Math.PI / 2, 0, 0]} position={[1.95, DECAL_Y, 0.1]} material={materials.headlightFloor} renderOrder={2}>
          <planeGeometry args={[2.6, 1.3]} />
        </mesh>

        <spotLight ref={poolLight} color={AMBER} position={[SERVICE_X, 1.35, barZ + 0.22]} target={targets.pool} angle={0.95} penumbra={1} decay={2} distance={6} intensity={0} />
        <spotLight
          ref={beam}
          color="#fff1d6"
          position={[HEADLIGHT_POSITION[0] + 0.02, HEADLIGHT_POSITION[1], HEADLIGHT_POSITION[2]]}
          target={targets.beam}
          angle={0.55}
          penumbra={1}
          decay={2}
          distance={9}
          intensity={0}
        />
        <pointLight ref={interior} color="#ffc074" position={[SERVICE_X, 1.3, barZ + 0.16]} decay={2} distance={LIGHTING.tramonto.interiorDistance} intensity={0} />
        <group ref={festoon}>
          {[-0.4, 0.4].map((dx) => (
            <pointLight key={dx} color="#ffcf7a" position={[SERVICE_X + dx, 1.5, barZ + 0.4]} distance={2.6} decay={2} intensity={0} />
          ))}
        </group>

        <group ref={bulbHalos}>
          {BULB_POSITIONS.map((p, i) => (
            <sprite key={i} position={p} material={materials.bulbHalo} layers={HALO_LAYER} renderOrder={4} />
          ))}
        </group>
        <sprite
          ref={headlightHalo}
          position={[HEADLIGHT_POSITION[0] + 0.03, HEADLIGHT_POSITION[1], HEADLIGHT_POSITION[2]]}
          scale={0.42}
          material={materials.headlightHalo}
          layers={HALO_LAYER}
          renderOrder={4}
        />
      </group>
    </group>
  );
}
